import { Reducer } from 'redux';

import * as Actions from '../actions/actions-consts';
import { Action } from '../../definitions/types';
import { DOMAIN_TLDS } from '../../modules/constants';
import {
  FreeDomainHint,
  DomainFieldPayload,
  DomainTransformedPayload,
  DomainValidationRequestedPayload,
  DomainValidationValidatedPayload,
  TldExtractedPayload,
  UsernameTransformedPayload,
  UsernameValidationPayload,
  ProductDomainHint,
  TransferDomainHint,
} from '../actions/domain';

export const FREE = 'free';
export const PRODUCT = 'product';
export const TRANSFER = 'transfer';

export interface Hints {
  [hint: number]: boolean; // Enums (DomainHint) can not yet be used as keys - falling back to `number`
}

export interface DomainField {
  name: string;
  rawValue: string;
  value: string;
  error: string;
  validated: boolean;
  validationPending: boolean;
  available: boolean;
  tld?: string;
  free?: boolean;
  hints?: Hints;
}

export interface DomainFieldReducer {
  // tslint:disable-next-line no-any
  (state: DomainField, action: Action<any>): DomainField;
}

export function getValue(state: DomainField): string {
  if (!state) {
    return null;
  }
  return state.value;
}

const createInitialState = (name: string): DomainField => {
  const baseInitialState = {
    name,
    rawValue: '',
    value: '',
    error: null as string,
    validated: false,
    validationPending: false,
    available: false,
  };

  switch (name) {
    case FREE:
    case TRANSFER:
      return baseInitialState;
    case PRODUCT:
      return Object.assign({}, baseInitialState, {
        tld: DOMAIN_TLDS[0],
      });
    default:
      throw new Error(`unknown domain type ${name}`);
  }
};

const setUsernameChanged = (
  state: DomainField,
  payload: string
): DomainField => {
  if (state.name !== FREE) {
    return state;
  }
  if (payload === state.value) {
    return state;
  }
  return Object.assign({}, state, {
    rawValue: payload,
    value: payload,
    validated: false,
    available: false,
    error: null,
    hints: null,
  });
};

const setUsernameTransformed = (
  state: DomainField,
  payload: UsernameTransformedPayload
): DomainField => {
  const { name, rawValue, value } = state;
  if (name !== FREE) {
    return state;
  }
  const { raw, transformed } = payload;
  if (raw === rawValue && transformed === value) {
    return state;
  }
  return Object.assign({}, state, {
    rawValue: raw,
    value: transformed,
  });
};

const setUsernameValidation = (
  state: DomainField,
  payload: UsernameValidationPayload
): DomainField => {
  if (state.name !== FREE) {
    return state;
  }
  const { error, value } = payload;
  return Object.assign({}, state, {
    value: value,
    error: error,
    validated: true,
    validationPending: false,
    available: !error,
  });
};

const setDomainFieldChanged = (
  state: DomainField,
  payload: DomainFieldPayload
): DomainField => {
  if (state.name !== payload.name) {
    return state;
  }
  if (payload.value === state.value) {
    return state;
  }
  return Object.assign({}, state, {
    rawValue: payload.value,
    value: payload.value,
    validated: false,
    available: false,
    error: null,
    hints: null,
  });
};

const setDomainTld = (state: DomainField, payload: string): DomainField => {
  if (state.name !== PRODUCT) {
    return state;
  }

  if (state.tld === payload) {
    return state;
  }

  return Object.assign({}, state, {
    tld: payload,
    error: null,
    hints: null,
    available: false,
    validated: false,
  });
};

function stripTldFromDomain(tld: string, domain: string): string {
  return domain.replace(new RegExp(`${tld}$`), '');
}

const setDomainFieldValidation = (
  state: DomainField,
  payload: DomainValidationValidatedPayload
): DomainField => {
  const { error, domain, domainType } = payload;
  if (state.name !== domainType) {
    return state;
  }
  const validatedDomain =
    domainType === PRODUCT ? stripTldFromDomain(state.tld, domain) : domain;

  return Object.assign({}, state, {
    value: validatedDomain,
    error,
    validationPending: false,
    validated: true,
    available: !error,
  });
};

const setDomainTransformed = (
  state: DomainField,
  payload: DomainTransformedPayload
): DomainField => {
  if (state.name !== PRODUCT) {
    return state;
  }

  if (state.value === payload.transformedValue) {
    return state;
  }
  return Object.assign({}, state, { value: payload.transformedValue });
};

const setExtractedTld = (
  state: DomainField,
  payload: TldExtractedPayload
): DomainField => {
  if (state.name !== PRODUCT) {
    return state;
  }
  const { tld, value } = payload;

  if (state.value === value && state.tld === tld) {
    return state;
  }
  return Object.assign({}, state, { value, tld });
};

const setUsernameValidationRequested = (state: DomainField): DomainField => {
  if (state.name !== FREE) {
    return state;
  }
  if (state.validationPending) {
    return state;
  }
  return Object.assign({}, state, { validationPending: true });
};

const setDomainfieldValidationRequested = (
  state: DomainField,
  payload: DomainValidationRequestedPayload
): DomainField => {
  if (state.name !== payload.activeType) {
    return state;
  }
  if (state.validationPending) {
    return state;
  }
  return Object.assign({}, state, { validationPending: true });
};

const resetValidationPending = (state: DomainField): DomainField => {
  if (!state.validationPending) {
    return state;
  }
  return Object.assign({}, state, {
    validationPending: false,
  });
};

const setHint = (
  state: DomainField,
  hint: FreeDomainHint | ProductDomainHint | TransferDomainHint
): DomainField => {
  if (state.hints && state.hints[hint]) {
    return state;
  }
  return Object.assign({}, state, {
    hints: Object.assign({}, state.hints, { [hint]: true }),
  });
};

const setFreeDomainHint = (
  state: DomainField,
  hint: FreeDomainHint
): DomainField => {
  if (state.name !== FREE) {
    return state;
  }
  return setHint(state, hint);
};

const setProductDomainHint = (
  state: DomainField,
  hint: ProductDomainHint
): DomainField => {
  if (state.name !== PRODUCT) {
    return state;
  }
  return setHint(state, hint);
};

const setTransferDomainHint = (
  state: DomainField,
  hint: TransferDomainHint
): DomainField => {
  if (state.name !== TRANSFER) {
    return state;
  }
  return setHint(state, hint);
};

const createDomainFieldReducer = (name: string): Reducer<DomainField> => {
  const initialState = createInitialState(name);
  // tslint:disable-next-line no-any
  return (state: DomainField = initialState, action: Action<any>) => {
    switch (action.type) {
      case Actions.USERNAME_VALIDATION_REQUESTED:
        return setUsernameValidationRequested(state);
      case Actions.DOMAIN_VALIDATION_REQUESTED:
        return setDomainfieldValidationRequested(state, action.payload);
      case Actions.USERNAME_VALUE_VALIDATED:
        return setUsernameValidation(state, action.payload);
      case Actions.DOMAIN_VALUE_TRANSFORMED:
        return setDomainTransformed(state, action.payload);
      case Actions.PRODUCT_TLD_EXTRACTED:
        return setExtractedTld(state, action.payload);
      case Actions.DOMAIN_VALIDATION_VALIDATED:
        return setDomainFieldValidation(state, action.payload);
      case Actions.GENERAL_REQUEST_FAILED:
      case Actions.CLEAR_TEMPORARY_STATE:
        return resetValidationPending(state);
      case Actions.USERNAME_VALUE_CHANGED:
        return setUsernameChanged(state, action.payload);
      case Actions.USERNAME_VALUE_TRANSFORMED:
        return setUsernameTransformed(state, action.payload);
      case Actions.DOMAIN_FIELD_VALUE_CHANGED:
        return setDomainFieldChanged(state, action.payload);
      case Actions.TLD_VALUE_CHANGED:
        return setDomainTld(state, action.payload);
      case Actions.SHOW_FREE_DOMAIN_HINT:
        return setFreeDomainHint(state, action.payload);
      case Actions.SHOW_PRODUCT_DOMAIN_HINT:
        return setProductDomainHint(state, action.payload);
      case Actions.SHOW_TRANSFER_DOMAIN_HINT:
        return setTransferDomainHint(state, action.payload);
      default:
        return state;
    }
  };
};

export default createDomainFieldReducer;
